/**
 * Parse GitHub-flavored markdown pipe tables into headers + body rows.
 */

export type ParsedMarkdownTable = {
  headers: string[];
  rows: string[][];
};

function splitPipeRow(line: string): string[] {
  const trimmed = line.trim();
  const inner =
    trimmed.startsWith("|") && trimmed.endsWith("|")
      ? trimmed.slice(1, -1)
      : trimmed.startsWith("|")
        ? trimmed.slice(1)
        : trimmed;
  return inner.split("|").map((cell) => cell.trim());
}

function isAlignmentRow(cells: string[]): boolean {
  if (cells.length === 0) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s+/g, "")));
}

/** True when text looks like a markdown pipe table (header + separator + ≥1 body row). */
export function looksLikeMarkdownTable(text: string): boolean {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return false;
  if (!lines.every((line) => line.includes("|"))) return false;
  const header = splitPipeRow(lines[0]);
  const separator = splitPipeRow(lines[1]);
  return header.length > 0 && isAlignmentRow(separator);
}

export function parseMarkdownTable(text: string): ParsedMarkdownTable | null {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return null;

  const headerCells = splitPipeRow(lines[0]);
  if (headerCells.length === 0) return null;

  let bodyStart = 1;
  if (isAlignmentRow(splitPipeRow(lines[1]))) {
    bodyStart = 2;
  }

  if (bodyStart >= lines.length) return null;

  const rows: string[][] = [];
  for (let i = bodyStart; i < lines.length; i += 1) {
    if (!lines[i].includes("|")) break;
    const cells = splitPipeRow(lines[i]);
    // Pad / trim to header width for stable columns
    const normalized = headerCells.map((_, idx) => cells[idx] ?? "");
    rows.push(normalized);
  }

  if (rows.length === 0) return null;

  return { headers: headerCells, rows };
}

/** Pull table source from a Portable Text `normal` block (legacy importer stored tables as code spans). */
export function extractMarkdownTableFromBlock(block: unknown): ParsedMarkdownTable | null {
  if (!block || typeof block !== "object") return null;
  const children = (block as { children?: unknown }).children;
  if (!Array.isArray(children)) return null;

  const text = children
    .map((child) =>
      child && typeof child === "object" && typeof (child as { text?: unknown }).text === "string"
        ? (child as { text: string }).text
        : "",
    )
    .join("")
    .trim();

  if (!looksLikeMarkdownTable(text)) return null;
  return parseMarkdownTable(text);
}
